import PolicyDependentButton from 'components/molecules/PolicyDependentButtons/PolicyDependentButton';
import {
  AssessmentStageFragmentFragment,
  AssessmentStageKind,
  InterviewStageFragment,
  ProjectAction,
  ProjectPageProjectFragment,
  ProjectStatus,
  ShortlistStageBaseFragment,
  useProjectPageCompanyQuery,
} from 'generated/graphql';
import { useProjectActionsValidation } from 'hooks/useProjectPolicyActions/useProjectPolicyActions';
import { useQueryParams } from 'hooks/useQueryParams';
import {
  useCognitiveSkillsFromRoles,
  useSkillsFromProjectJobRoles,
} from 'hooks/useSkillsFromProjectJobRoles';
import { Fragment, useMemo, useState } from 'react';
import DeleteShortlistStageButton from 'views/Project/components/Stages/DeleteShortlistStageButton/DeleteShortlistStageButton';
import {
  INTERVIEW_STAGE_DEFAULT_VALUES,
  INTERVIEW_STAGE_DURATION_DEFAULT_VALUE,
  INTERVIEW_STAGE_FOCUS_DEFAULT_VALUE,
  INTERVIEW_STAGE_NAME_DEFAULT_VALUE,
  stageKindLabel,
} from 'views/Project/components/Stages/helpers';
import InsertStageButton from 'views/Project/components/Stages/InsertStageButton/InsertStageButton';
import InterviewStageForm from 'views/Project/components/Stages/InterviewStageForm/InterviewStageForm';
import InterviewStageOverview from 'views/Project/components/Stages/InterviewStageOverview/InterviewStageOverview';
import ShortlistStageForm from 'views/Project/components/Stages/ShortlistStageForm/ShortlistStageForm';
import DeleteInterviewStage from 'views/Project/components/Stages/StageActionButtons/DeleteInterviewStage';
import DeleteStage from 'views/Project/components/Stages/StageActionButtons/DeleteStage';
import { Button } from '@spotted-zebra-uk/ui-components';
import styles from './Stage.module.scss';
import StageActions from './StageActions';
import StageForm from './StageForm/StageForm';
import {
  getInitialValuesForCreateStage,
  getInitialValuesForEditStage,
} from './StageForm/StageForm.helper';
import StageScoringPanel from './StageScoringPanel/StageScoringPanel';
import StageHeader from './StageHeader/StageHeader';
import StageOverview from './StageOverview/StageOverview';

type StageProps = {
  companyId: number;
  projectId: number;
  projectSubId: string;
  interviewStages: InterviewStageFragment[];
  projectStatus: ProjectStatus;
  assessmentStages: AssessmentStageFragmentFragment[];
  shortlistStages: ShortlistStageBaseFragment[];
  /** Stage models carrying `scoringConfig`; matched to stages by sub id. */
  stageModels?: ProjectPageProjectFragment['assessmentStages'];
};

export type StageType = 'interview' | 'assessment' | 'shortlist';

const dataTestIds = {
  editAssessmentStageButton: 'stage-header__edit-assessment-stage-button',
  deleteAssessmentStageButton: 'stage-header__delete-assessment-stage-button',
  editInterviewStageButton: 'stage-header__edit-interview-stage-button',
  deleteInterviewStageButton: 'stage-header__delete-interview-stage-button',
};

const publishedProjectTooltipContent =
  'There must be at least one stage on a published project.';

const Stage = ({
  companyId,
  projectId,
  interviewStages,
  projectSubId,
  projectStatus,
  assessmentStages,
  shortlistStages,
  stageModels,
}: StageProps) => {
  const [params] = useQueryParams(['devmode']);
  const isDevMode = params.devmode === 'true';

  const [isEditing, setIsEditing] = useState<StageType | null>(null);
  const [editInterviewStage, setEditInterviewStage] =
    useState<InterviewStageFragment | null>(null);
  const [editAssessmentStage, setEditAssessmentStage] =
    useState<AssessmentStageFragmentFragment | null>(null);
  const [isInsertingInterviewStage, setIsInsertingInterviewStage] =
    useState(false);
  // Which kind the assessment stage form is creating. The form has no kind
  // field: the Insert stage menu entry decides it.
  const [insertAssessmentStageKind, setInsertAssessmentStageKind] =
    useState<AssessmentStageKind>(AssessmentStageKind.Assessment);

  const { data: projectPageCompanyData } = useProjectPageCompanyQuery({
    variables: { id: companyId },
  });

  const { projectPolicyActions } = useProjectActionsValidation({
    projectId,
  });

  const {
    projectJobRoles,
    softSkillsByCategory,
    productSolution,
    hasRoleWithOnlyOneCognitiveSkill,
  } = useSkillsFromProjectJobRoles({
    projectId,
  });

  const { cognitiveSkillsVersion, prefillCognitiveMode } =
    useCognitiveSkillsFromRoles(
      softSkillsByCategory,
      productSolution,
      hasRoleWithOnlyOneCognitiveSkill
    );

  const company = projectPageCompanyData?.company;

  const isMultiMatch = projectJobRoles.length > 1;

  const prefillValues = useMemo(
    () =>
      editAssessmentStage
        ? getInitialValuesForEditStage(editAssessmentStage, company)
        : getInitialValuesForCreateStage(
            company,
            prefillCognitiveMode,
            insertAssessmentStageKind
          ),
    [
      editAssessmentStage,
      prefillCognitiveMode,
      company,
      insertAssessmentStageKind,
    ]
  );

  const handleEditStage = (type: StageType) => {
    if (type === 'assessment') {
      setInsertAssessmentStageKind(AssessmentStageKind.Assessment);
    }
    setIsEditing(type);
  };

  const handleInsertAiInterviewStage = () => {
    setInsertAssessmentStageKind(AssessmentStageKind.AiInterview);
    setIsEditing('assessment');
  };

  const handleEditAssessmentStage = (
    stage: AssessmentStageFragmentFragment
  ) => {
    setEditAssessmentStage(stage);
    setIsEditing('assessment');
  };

  const handleEditInterviewStage = (subId: string) => {
    const interviewStageToEdit = interviewStages.find(
      stage => stage.subId === subId
    );

    if (interviewStageToEdit) {
      setEditInterviewStage(interviewStageToEdit);
      setIsEditing('interview');
    }
  };

  const handleInsertStage = () => {
    setIsInsertingInterviewStage(true);
  };

  const handleCancelForm = () => {
    setIsEditing(null);
    setEditInterviewStage(null);
    setEditAssessmentStage(null);
    setIsInsertingInterviewStage(false);
    setInsertAssessmentStageKind(AssessmentStageKind.Assessment);
  };

  const sortedInterviewStages = useMemo(() => {
    return [...interviewStages].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    );
  }, [interviewStages]);

  const sortedAssessmentStages = useMemo(() => {
    return [...assessmentStages].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    );
  }, [assessmentStages]);

  // Counted per kind: several assessment stages are allowed, but only one AI
  // interview stage, and neither cap should count the other's stages.
  const assessmentKindStageCount = useMemo(
    () =>
      assessmentStages.filter(s => s.kind === AssessmentStageKind.Assessment)
        .length,
    [assessmentStages]
  );

  const aiInterviewKindStageCount = useMemo(
    () =>
      assessmentStages.filter(s => s.kind === AssessmentStageKind.AiInterview)
        .length,
    [assessmentStages]
  );

  const renderScoringPanel = (stage: AssessmentStageFragmentFragment) => {
    if (stage.kind !== AssessmentStageKind.Assessment) return null;
    const stageModel = stageModels?.find(model => model.subId === stage.id);
    if (!stageModel) return null;
    return (
      <StageScoringPanel
        stageId={stageModel.id}
        projectId={projectId}
        scoringConfig={stageModel.scoringConfig}
        requiredCefrLevel={stage.actions.englishProficiency?.requiredCefrLevel}
      />
    );
  };

  const showInterviewStageIndex = sortedInterviewStages.length > 1;

  const isInsertStageButtonDisabled = useMemo(() => {
    return (
      Boolean(isEditing) ||
      (!projectPolicyActions[ProjectAction.CreateInterviewStage] &&
        !projectPolicyActions[ProjectAction.CreateAssessmentStage])
    );
  }, [isEditing, projectPolicyActions]);

  return (
    <div className={styles.container}>
      <StageHeader
        title="Stages"
        primaryButton={
          <InsertStageButton
            projectJobRoles={projectJobRoles}
            assessmentKindStageCount={assessmentKindStageCount}
            aiInterviewKindStageCount={aiInterviewKindStageCount}
            projectPolicyActions={projectPolicyActions}
            handleEditStage={handleEditStage}
            handleInsertStage={handleInsertStage}
            handleInsertAiInterviewStage={handleInsertAiInterviewStage}
            disabled={isInsertStageButtonDisabled}
            interviewStages={sortedInterviewStages}
            shortlistStages={shortlistStages}
            isShortlistingEnabled={Boolean(company?.isShortlistingEnabled)}
          />
        }
      />
      <div className={styles.stagesContent}>
        {((isEditing === 'interview' && editInterviewStage) ||
          isInsertingInterviewStage) && (
          <InterviewStageForm
            projectId={projectId}
            interviewStageSubId={
              isInsertingInterviewStage ? undefined : editInterviewStage?.subId
            }
            projectSubId={projectSubId}
            onCancel={handleCancelForm}
            onSubmit={handleCancelForm}
            initialValues={
              isInsertingInterviewStage
                ? {
                    ...INTERVIEW_STAGE_DEFAULT_VALUES,
                    softSkills: [],
                    technicalSkills: [],
                  }
                : {
                    duration:
                      editInterviewStage?.duration ??
                      INTERVIEW_STAGE_DURATION_DEFAULT_VALUE,
                    focus:
                      editInterviewStage?.focus ??
                      INTERVIEW_STAGE_FOCUS_DEFAULT_VALUE,
                    name:
                      editInterviewStage?.name ??
                      INTERVIEW_STAGE_NAME_DEFAULT_VALUE,
                    softSkills: editInterviewStage?.softSkills ?? [],
                    technicalSkills: editInterviewStage?.technicalSkills ?? [],
                  }
            }
          />
        )}
        {isEditing !== 'interview' &&
          interviewStages.length > 0 &&
          sortedInterviewStages.map((interviewStage, index) => (
            <>
              <StageHeader
                title={`${
                  showInterviewStageIndex
                    ? `${index + 1}. Interview stage `
                    : 'Interview stage'
                }`}
                deleteButton={
                  <PolicyDependentButton
                    id={dataTestIds.deleteInterviewStageButton}
                    disabled={
                      !projectPolicyActions[ProjectAction.RemoveInterviewStage]
                    }
                    projectStatus={projectStatus}
                    customTooltipContent={publishedProjectTooltipContent}
                    renderItem={disabled => (
                      <DeleteInterviewStage
                        subId={interviewStage.subId}
                        projectStatus={projectStatus}
                        disabled={disabled}
                        projectId={projectId}
                      />
                    )}
                  />
                }
                primaryButton={
                  <PolicyDependentButton
                    id={dataTestIds.editInterviewStageButton}
                    projectStatus={projectStatus}
                    disabled={
                      !projectPolicyActions[
                        ProjectAction.UpdateInterviewStageName
                      ]
                    }
                    renderItem={disabled => (
                      <Button
                        onClick={() =>
                          handleEditInterviewStage(interviewStage.subId)
                        }
                        size="medium"
                        disabled={disabled}
                      >
                        Edit
                      </Button>
                    )}
                  />
                }
              />
              <InterviewStageOverview interviewStage={interviewStage} />
            </>
          ))}

        {isEditing === 'assessment' ? (
          <StageForm
            stageId={editAssessmentStage?.id}
            projectId={projectId}
            isMultiMatch={isMultiMatch}
            onCancel={handleCancelForm}
            isLoading={false}
            onSubmit={handleCancelForm}
            initialValues={prefillValues}
            companyExpiryOption={company?.assessmentExpiryOption}
            cognitiveSkillsVersion={cognitiveSkillsVersion}
            hasRoleOnlyOneCognitiveSkill={hasRoleWithOnlyOneCognitiveSkill}
            companyHideReports={Boolean(company?.hideReports)}
            projectStatus={projectStatus}
          />
        ) : (
          sortedAssessmentStages.map(stage => (
            <Fragment key={stage.id}>
              <StageHeader
                title={`${stageKindLabel(stage.kind)} stage`}
                primaryButton={
                  <PolicyDependentButton
                    id={dataTestIds.editAssessmentStageButton}
                    projectStatus={projectStatus}
                    disabled={
                      !projectPolicyActions[
                        ProjectAction.UpdateAssessmentStageName
                      ]
                    }
                    renderItem={disabled => (
                      <Button
                        onClick={() => handleEditAssessmentStage(stage)}
                        size="medium"
                        disabled={disabled}
                      >
                        Edit
                      </Button>
                    )}
                  />
                }
                deleteButton={
                  <PolicyDependentButton
                    id={dataTestIds.deleteAssessmentStageButton}
                    projectStatus={projectStatus}
                    customTooltipContent={publishedProjectTooltipContent}
                    disabled={
                      !projectPolicyActions[ProjectAction.RemoveAssessmentStage]
                    }
                    renderItem={disabled => (
                      <DeleteStage
                        projectId={projectId}
                        id={stage.id}
                        projectStatus={projectStatus}
                        disabled={disabled}
                      />
                    )}
                  />
                }
              />
              <StageOverview
                stage={stage}
                projectId={projectId}
                projectSubId={projectSubId}
                hasCompanyExpiryOption={Boolean(
                  company?.assessmentExpiryOption
                )}
                cognitiveSkillsVersion={cognitiveSkillsVersion}
                companyHideReports={Boolean(company?.hideReports)}
                isDevMode={isDevMode}
                projectStatus={projectStatus}
              />
              <StageActions
                stageId={stage.id}
                projectId={projectId}
                assessmentStage={stage}
              />
              {renderScoringPanel(stage)}
            </Fragment>
          ))
        )}
        {isEditing === 'shortlist' && (
          <ShortlistStageForm
            onCancel={handleCancelForm}
            onSubmit={handleCancelForm}
            projectSubId={projectSubId}
          />
        )}
        {isDevMode &&
          isEditing !== 'shortlist' &&
          shortlistStages.map(shortlistStage => (
            <>
              <StageHeader
                title={`Shortlist stage - ${shortlistStage.name}`}
                deleteButton={
                  <DeleteShortlistStageButton
                    projectStatus={projectStatus}
                    disabled={
                      !projectPolicyActions[ProjectAction.RemoveShortlistStage]
                    }
                    shortlistStageSubId={shortlistStage.subId}
                    projectId={projectId}
                  />
                }
              />
            </>
          ))}
      </div>
    </div>
  );
};

export default Stage;
